import { Controller } from "@hotwired/stimulus"

// 챗봇 생성 페이지의 AI 도우미 기능을 담당하는 컨트롤러
export default class extends Controller {
  static targets = ["name", "description", "systemPrompt", "userPrompt", "chatbotGoal", "aiButtonText", "autoCreateButtonText", "tabContent"]
  static values = { 
    activeField: String,
    activeTab: String,
    errorTitle: String,
    promptRequired: String,
    fieldRequired: String,
    generating: String,
    generatingBot: String,
    createSuccessTitle: String,
    createSuccessBody: String,
    formNotFoundTitle: String,
    formNotFoundBody: String,
    buttonCreateLabel: String
  }
  
  connect() {
    console.log("Chatbot creator controller connected")
    // 디버그: 폼 존재 여부 로그
    const idForm = document.getElementById('chatbot-form')
    console.log('[chatbot-creator] form#chatbot-form present?', !!idForm)
    const innerForm = this.element.querySelector('form')
    console.log('[chatbot-creator] form inside controller present?', !!innerForm)
    
    // 상태 초기화 및 복원
    this.initializeState()
    
    // 탭 상태 복원
    this.restoreTabState()
    
    // Turbo 이벤트 리스너 설정
    this.setupTurboEventListeners()
    
    console.log("Chatbot creator initialization completed")
  }

  disconnect() {
    console.log("Chatbot creator controller disconnected")
    
    // 이벤트 리스너 정리
    this.removeTurboEventListeners()
  }

  // 상태 초기화
  initializeState() {
    // 기본값 설정
    this.activeFieldValue = this.activeFieldValue || "name"
    this.activeTabValue = this.activeTabValue || "settings"
    
    console.log("Initial state - Active field:", this.activeFieldValue, "Active tab:", this.activeTabValue)
  }

  // 탭 상태 복원
  restoreTabState() {
    // URL 파라미터에서 탭 정보 확인
    const urlParams = new URLSearchParams(window.location.search)
    const tabFromUrl = urlParams.get('tab')
    
    if (tabFromUrl && this.isValidTab(tabFromUrl)) {
      this.activeTabValue = tabFromUrl
    }
    
    // DOM에서 현재 활성 탭 확인
    const activeTabButton = this.element.querySelector('[data-tab].border-indigo-500')
    if (activeTabButton) {
      this.activeTabValue = activeTabButton.dataset.tab
    }
    
    // 탭 상태 적용
    this.applyTabState()
    
    console.log("Tab state restored:", this.activeTabValue)
  }

  // 유효한 탭인지 확인
  isValidTab(tabName) {
    const validTabs = ['settings', 'ai-assistant', 'preview']
    return validTabs.includes(tabName)
  }

  // 탭 상태 적용
  applyTabState() {
    // 탭 버튼 상태 업데이트
    const tabButtons = this.element.querySelectorAll('[data-tab]')
    tabButtons.forEach(button => {
      if (button.dataset.tab === this.activeTabValue) {
        button.classList.add('border-b-2', 'border-indigo-500', 'text-indigo-700')
        button.classList.remove('text-gray-500')
      } else {
        button.classList.remove('border-b-2', 'border-indigo-500', 'text-indigo-700')
        button.classList.add('text-gray-500')
      }
    })
    
    // 탭 컨텐츠 상태 업데이트
    this.tabContentTargets.forEach(content => {
      if (content.dataset.tab === this.activeTabValue) {
        content.classList.remove('hidden')
      } else {
        content.classList.add('hidden')
      }
    })
  }

  // Turbo 이벤트 리스너 설정
  setupTurboEventListeners() {
    this.handleBeforeCache = this.handleBeforeCache.bind(this)
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)
  }

  // Turbo 이벤트 리스너 제거
  removeTurboEventListeners() {
    if (this.handleBeforeCache) {
      document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
      this.handleBeforeCache = null
    }
  }

  // Turbo 캐시 전 상태 저장
  handleBeforeCache() {
    console.log("Chatbot creator: saving state before cache")
    
    // URL에 현재 탭 정보 저장
    if (this.activeTabValue !== 'settings') {
      const url = new URL(window.location)
      url.searchParams.set('tab', this.activeTabValue)
      window.history.replaceState(null, '', url)
    }
  }
  
  // 탭 전환 기능
  switchTab(event) {
    const tabName = event.currentTarget.dataset.tab
    
    if (!this.isValidTab(tabName)) {
      console.warn("Invalid tab name:", tabName)
      return
    }
    
    console.log(`Switching to tab: ${tabName}`)
    
    // 상태 업데이트
    this.activeTabValue = tabName
    
    // UI 업데이트
    this.applyTabState()
    
    // URL 업데이트 (브라우저 히스토리에 추가하지 않음)
    if (tabName !== 'settings') {
      const url = new URL(window.location)
      url.searchParams.set('tab', tabName)
      window.history.replaceState(null, '', url)
    } else {
      const url = new URL(window.location)
      url.searchParams.delete('tab')
      window.history.replaceState(null, '', url)
    }
  }
  
  // 활성 필드 설정
  setActiveField(event) {
    const fieldName = event.currentTarget.dataset.field
    
    if (!fieldName) {
      console.warn("Field name not found in data-field attribute")
      return
    }
    
    this.activeFieldValue = fieldName
    console.log(`Active field set to: ${this.activeFieldValue}`)
    
    // 시각적 피드백 추가 (선택적)
    this.highlightActiveField(event.currentTarget)
  }

  // 활성 필드 하이라이트
  highlightActiveField(fieldElement) {
    // 모든 필드에서 활성 상태 제거
    const allFields = this.element.querySelectorAll('[data-field]')
    allFields.forEach(field => {
      field.classList.remove('ring-2', 'ring-indigo-500')
    })
    
    // 현재 필드에 활성 상태 추가
    if (fieldElement) {
      fieldElement.classList.add('ring-2', 'ring-indigo-500')
      
      // 3초 후 하이라이트 제거
      setTimeout(() => {
        fieldElement.classList.remove('ring-2', 'ring-indigo-500')
      }, 3000)
    }
  }
  
  // AI로 작성하기
  generateWithAI() {
    const prompt = this.userPromptTarget.value.trim()
    if (!prompt) {
      this.showToast(this.errorTitleValue || 'Error', this.promptRequiredValue || 'Please enter a prompt.', 'error')
      return
    }
    
    // 활성 필드 확인
    if (!this.activeFieldValue) {
      this.showToast(this.errorTitleValue || 'Error', this.fieldRequiredValue || 'Select a target field first.', 'error')
      return
    }
    
    // 로딩 상태 표시
    if (this.hasAiButtonTextTarget) {
      this.aiButtonTextTarget.textContent = (this.generatingValue || 'Generating...')
    }
    
    // API 호출 (실제로는 서버 API를 호출해야 함)
    this.mockAIWritingAssistant(prompt, this.activeFieldValue)
  }
  
  // 예시 프롬프트 사용
  useExamplePrompt(event) {
    const prompt = event.currentTarget.dataset.prompt;
    this.chatbotGoalTarget.value = prompt;
    
    // 텍스트에리어에 포커스
    this.chatbotGoalTarget.focus();
  }
  
  // AI로 챗봇 자동 생성
  createChatbotWithAI(event) {
    const goal = this.chatbotGoalTarget.value.trim();
    if (!goal) {
      this.showToast(this.errorTitleValue || 'Error', this.promptRequiredValue || 'Please enter a goal.', 'error');
      return;
    }
    
    // 로딩 상태 표시
    this.autoCreateButtonTextTarget.textContent = (this.generatingValue || 'Generating...');
    
    // 실제 구현에서는 서버로 API 요청을 보내야 함
    setTimeout(() => {
      let result = {};
      
      // 목적에 따라 다른 결과 생성
      if (goal.includes("제품 문의") || goal.includes("제품 정보") || goal.toLowerCase().includes("product")) {
        result = {
          name: "product_inquiry_dm_bot_" + Date.now(),
          description: goal,
          systemPrompt: "You are a chatbot that replies to Instagram comments via a one-time DM. When someone asks about products, provide details, pricing, features, and how to purchase. Keep responses clear and concise, and include support contact info for follow-ups."
        };
      } else if (goal.includes("고객 지원") || goal.includes("배송") || goal.includes("반품") || goal.toLowerCase().includes("support")) {
        result = {
          name: "customer_support_dm_bot_" + Date.now(),
          description: goal,
          systemPrompt: "You are a customer support chatbot replying via one-time DM. Provide clear steps for shipping, returns, exchanges, and refunds. Keep the tone friendly and professional, and guide users through each step."
        };
      } else if (goal.includes("프로모션") || goal.includes("할인") || goal.includes("이벤트") || goal.toLowerCase().includes("promo") || goal.toLowerCase().includes("discount") || goal.toLowerCase().includes("event")) {
        result = {
          name: "promotion_dm_bot_" + Date.now(),
          description: goal,
          systemPrompt: "You are a promotion chatbot replying via one-time DM. Share discount details, event schedules, and coupon codes. Use an engaging tone and clearly explain the validity period and how to redeem offers."
        };
      } else {
        result = {
          name: (goal.substring(0, 10) || 'chatbot') + "_bot_" + Date.now(),
          description: goal,
          systemPrompt: "You are an expert in " + goal.substring(0, 15) + ". Provide accurate, helpful answers and share best practices. Keep responses concise while covering all necessary details."
        };
      }
      
      // 모든 필드에 결과 적용
      this.nameTarget.value = result.name;
      this.descriptionTarget.value = result.description;
      this.systemPromptTarget.value = result.systemPrompt;
      
      // 성공 메시지 표시
      this.showToast(this.createSuccessTitleValue || 'Success', this.createSuccessBodyValue || 'AI created configuration.', 'success');
      
    // 폼 자동 제출
      // 우선 클릭한 버튼 기준으로 가장 가까운 폼을 찾음
      const form = (event && event.currentTarget && event.currentTarget.closest('form')) || this.findChatbotForm()
      if (form) {
        // 로딩 상태 표시
        if (this.hasAutoCreateButtonTextTarget) {
          this.autoCreateButtonTextTarget.textContent = (this.generatingBotValue || 'Creating bot...')
        }
        
        // CSRF 토큰 확인 및 갱신
        this.validateAndRefreshCSRFToken(form)
        
        // 필드 값이 적용되었음을 Turbo에 알리기 위해 change 이벤트 발생
        this.triggerChangeEvents()
        
        // 잠시 대기 후 폼 제출 (Turbo 호환성을 위해 requestSubmit 사용)
        setTimeout(() => {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit()
          } else {
            // requestSubmit이 없는 브라우저 호환
            form.submit()
          }
        }, 1000)
      } else {
        // 로딩 상태 해제
        if (this.hasAutoCreateButtonTextTarget) {
          this.autoCreateButtonTextTarget.textContent = (this.buttonCreateLabelValue || 'Create with AI')
        }
        this.showToast(this.formNotFoundTitleValue || 'Error', this.formNotFoundBodyValue || 'Form not found.', 'error')
      }
    }, 1500) // 1.5초 지연으로 로딩 효과
  }

  // 폼 탐색을 보다 견고하게 수행
  findChatbotForm() {
    // 1) 명시적 ID
    let form = document.getElementById('chatbot-form')
    console.log('[chatbot-creator] try id selector ->', !!form)
    if (form) return form

    // 2) 컨트롤러 영역 하위 첫 번째 폼
    form = this.element.querySelector('form')
    console.log('[chatbot-creator] try element.querySelector(form) ->', !!form)
    if (form) return form

    // 3) chatbots#create로 향하는 폼 추정
    form = Array.from(document.querySelectorAll('form')).find(f => {
      const action = f.getAttribute('action') || ''
      return action.includes('/chatbots') && !action.match(/\/(\d+)(\/|$)/)
    })
    console.log('[chatbot-creator] try action includes /chatbots ->', !!form)
    if (form) return form

    // 4) 필드명으로 식별
    form = Array.from(document.querySelectorAll('form')).find(f => f.querySelector('input[name="chatbot[name]"]') || f.querySelector('textarea[name="chatbot[prompt]"]'))
    console.log('[chatbot-creator] try fields presence ->', !!form)
    return form || null
  }

  // CSRF 토큰 확인 및 갱신
  validateAndRefreshCSRFToken(form) {
    const metaToken = document.querySelector('meta[name="csrf-token"]')?.content
    const formToken = form.querySelector('input[name="authenticity_token"]')?.value
    
    if (!metaToken) {
      console.error('CSRF meta token missing')
      return false
    }
    
    if (!formToken) {
      console.warn('Form CSRF token missing, adding from meta tag')
      // meta 태그에서 토큰 복사
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = metaToken
      form.appendChild(tokenInput)
      return true
    }
    
    if (metaToken !== formToken) {
      console.warn('CSRF token mismatch, updating form token')
      form.querySelector('input[name="authenticity_token"]').value = metaToken
    }
    
    return true
  }

  // 필드 값 변경 이벤트 발생 (Turbo 호환성)
  triggerChangeEvents() {
    const fields = [
      this.hasNameTarget ? this.nameTarget : null,
      this.hasDescriptionTarget ? this.descriptionTarget : null,
      this.hasSystemPromptTarget ? this.systemPromptTarget : null
    ].filter(Boolean)
    
    fields.forEach(field => {
      field.dispatchEvent(new Event('change', { bubbles: true }))
      field.dispatchEvent(new Event('input', { bubbles: true }))
    })
    
    console.log('Change events triggered for updated fields')
  }
  
  // 목업 AI 작성 도우미 (실제 구현에서는 서버 API 호출로 대체)
  mockAIWritingAssistant(prompt, field) {
    // 실제 구현에서는 서버로 API 요청을 보내야 함
    setTimeout(() => {
      let result = {};
      
      // 프롬프트에 따라 다른 결과 생성
      if (prompt.includes("마케팅")) {
        result = {
          name: "인스타그램 마케팅 어시스턴트",
          description: "인스타그램 마케팅 전략 수립과 콘텐츠 기획을 도와주는 전문 챗봇입니다. 해시태그 최적화, 포스팅 일정 관리, 인게이지먼트 향상 팁을 제공합니다.",
          systemPrompt: "당신은 인스타그램 마케팅 전문가입니다. 사용자의 인스타그램 마케팅 전략 수립과 콘텐츠 기획을 도와주세요. 해시태그 최적화, 포스팅 일정 관리, 인게이지먼트 향상을 위한 구체적인 조언을 제공하세요. 항상 최신 트렌드를 반영하고, 사용자의 목표와 타겟 오디언스에 맞는 맞춤형 전략을 제안하세요."
        };
      } else if (prompt.includes("고객")) {
        result = {
          name: "친절한 고객 서비스 봇",
          description: "고객 문의에 친절하고 정확하게 응답하는 챗봇입니다. 제품 정보, 주문 상태, 반품 및 교환 정책에 대한 안내를 제공합니다.",
          systemPrompt: "당신은 친절한 고객 서비스 담당자입니다. 항상 정중하고 도움이 되는 태도로 고객의 문의에 응답하세요. 제품 정보, 주문 상태, 반품 및 교환 정책에 대한 정확한 정보를 제공하고, 고객의 불만 사항을 공감하며 해결책을 제시하세요."
        };
      } else {
        result = {
          name: "다목적 비서 챗봇",
          description: "일정 관리, 정보 검색, 메모 작성 등 다양한 업무를 도와주는 개인 비서 챗봇입니다.",
          systemPrompt: "당신은 사용자의 개인 비서입니다. 일정 관리, 정보 검색, 메모 작성 등 다양한 업무를 도와주세요. 사용자의 요청에 정확하고 효율적으로 응답하며, 필요한 정보를 명확하게 전달하세요."
        };
      }
      
      // 필드에 따라 결과 적용
      if (field === "name" || field === "all") {
        this.nameTarget.value = result.name;
      }
      
      if (field === "description" || field === "all") {
        this.descriptionTarget.value = result.description;
      }
      
      if (field === "systemPrompt" || field === "all") {
        this.systemPromptTarget.value = result.systemPrompt;
      }
      
      // 로딩 상태 해제
      this.aiButtonTextTarget.textContent = "AI로 작성하기";
      
      // 성공 메시지 표시
      this.showToast("AI 작성 완료", "AI가 작성한 내용이 적용되었습니다.", "success");
    }, 1500); // 1.5초 지연으로 로딩 효과
  }
  
  // 목업 자동 챗봇 생성 (실제 구현에서는 서버 API 호출로 대체)
  mockAutoChatbotCreation(goal) {
    // 실제 구현에서는 서버로 API 요청을 보내야 함
    setTimeout(() => {
      let result = {};
      
      // 목적에 따라 다른 결과 생성
      if (goal.includes("마케팅")) {
        result = {
          name: "마케팅 전략 어드바이저",
          description: "디지털 마케팅 전략 수립과 실행을 도와주는 전문 챗봇입니다. 소셜 미디어 마케팅, 콘텐츠 마케팅, SEO 최적화 등 다양한 마케팅 채널에 대한 조언을 제공합니다.",
          systemPrompt: "당신은 디지털 마케팅 전문가입니다. 사용자의 마케팅 목표에 맞는 전략을 수립하고, 실행 계획을 제안하세요. 소셜 미디어 마케팅, 콘텐츠 마케팅, SEO 최적화, 이메일 마케팅 등 다양한 마케팅 채널에 대한 전문적인 조언을 제공하고, 마케팅 성과를 측정하고 개선하는 방법을 안내하세요."
        };
      } else if (goal.includes("교육")) {
        result = {
          name: "학습 도우미 튜터",
          description: "다양한 주제에 대한 학습을 도와주는 교육용 챗봇입니다. 개념 설명, 문제 해결, 학습 자료 추천 등을 제공합니다.",
          systemPrompt: "당신은 교육 전문가입니다. 사용자의 학습을 도우기 위해 다양한 주제에 대한 명확한 개념 설명, 단계별 문제 해결 방법, 맞춤형 학습 자료 추천을 제공하세요. 사용자의 이해 수준에 맞게 설명하고, 질문에 인내심을 갖고 응답하며, 학습 동기를 부여하는 긍정적인 피드백을 제공하세요."
        };
      } else if (goal.includes("예약") || goal.includes("예약 관리")) {
        result = {
          name: "예약관리_DM_챗봇_" + Date.now(),
          description: goal,
          systemPrompt: "당신은 인스타그램 댓글에 응답하는 예약 관리 챗봇입니다. 고객이 예약 문의나 예약 가능 시간에 대한 댓글을 남기면, 1회성 DM을 통해 예약 가능한 시간대, 예약 방법, 취소 정책 등을 상세히 안내해주세요. 답변은 명확하고 정확해야 하며, 고객이 필요로 하는 모든 정보를 포함해야 합니다. 메타 API를 통해 제공되는 1회성 DM 기능을 활용하여 고객의 예약 과정을 원활하게 도와주는 것이 목표입니다."
        };
      } else {
        result = {
          name: "인스타그램_댓글응답_챗봇_" + Date.now(),
          description: goal + "에 관한 인스타그램 댓글 응답 챗봇입니다.",
          systemPrompt: "당신은 인스타그램 댓글에 응답하는 1회성 DM 챗봇입니다. " + goal + " 분야와 관련된 댓글에 응답할 때, 정확하고 유용한 정보를 제공하세요. 메타 API를 통해 제공되는 1회성 DM 기능을 활용하여 댓글에 대한 상세한 응답을 제공하세요. 답변은 간결하면서도 필요한 모든 정보를 포함해야 하며, 고객이 추가 질문이 있을 경우 어떻게 연락할 수 있는지 안내하세요."
        };
      }
      
      // 모든 필드에 결과 적용
      this.nameTarget.value = result.name;
      this.descriptionTarget.value = result.description;
      this.systemPromptTarget.value = result.systemPrompt;
      
      // 폼 자동 제출
      const form = document.getElementById('chatbot-form');
      console.log('Form element:', form); // 디버깅용 로그 추가
      
      // 로딩 상태 표시
      this.autoCreateButtonTextTarget.textContent = "챗봇 생성 중...";
      
      // 성공 메시지 표시
      this.showToast("AI 챗봇 생성 완료", "AI가 챗봇 설정을 자동으로 생성했습니다.", "success");
      
      // 잠시 대기 후 폼 제출
      setTimeout(() => {
        try {
          // 폼 제출을 위한 전통적인 방법 사용
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          form.dispatchEvent(submitEvent);
        } catch (error) {
          console.error('Form submission error:', error);
          this.showToast("오류 발생", "폼 제출 중 오류가 발생했습니다.", "error");
          this.autoCreateButtonTextTarget.textContent = "AI로 챗봇 생성하기";
        }
      }, 1000);
    }, 2000); // 2초 지연으로 로딩 효과
  }
  
  // 토스트 메시지 표시 (실제 구현에서는 앱의 토스트 시스템 사용)
  showToast(title, message, type = "info") {
    // 기존 토스트 제거
    const existingToast = document.getElementById("toast-notification");
    if (existingToast) {
      existingToast.remove();
    }
    
    // 토스트 색상 설정
    let bgColor = "bg-blue-500";
    if (type === "success") bgColor = "bg-green-500";
    if (type === "error") bgColor = "bg-red-500";
    
    // 토스트 HTML 생성
    const toast = document.createElement("div");
    toast.id = "toast-notification";
    toast.className = `fixed top-4 right-4 ${bgColor} text-white p-4 rounded-lg shadow-lg z-50 transform transition-all duration-300 ease-in-out`;
    toast.innerHTML = `
      <div class="flex items-center">
        <div class="flex-1">
          <h3 class="font-bold">${title}</h3>
          <p class="text-sm">${message}</p>
        </div>
        <button class="ml-4 text-white hover:text-gray-200" onclick="this.parentElement.parentElement.remove()">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `;
    
    // 토스트 추가 및 자동 제거
    document.body.appendChild(toast);
    setTimeout(() => {
      if (document.getElementById("toast-notification")) {
        document.getElementById("toast-notification").remove();
      }
    }, 5000);
  }
}
